/**
 * HeartbeatCard — the once-a-day "approve once, runs on autopilot" card.
 *
 * Sits at the top of Home's Argo-suggests area. The PURE detectors
 * (src/lib/agent/heartbeat.ts) observe real workspace signals (past-due
 * invoices, slipped follow-ups, weekly-update cadence) and the picker
 * surfaces at most ONE proposal per day: headline, real evidence bullets,
 * what turning it on will do, and its cadence.
 *
 *   [Approve once] → enables that ONE scoped automation
 *     (src/lib/agent/automations.ts — it rides the existing live-schedule
 *     tick and only QUEUES drafts for approval), pushes a toast, and the
 *     card collapses to a small "Active automations (N)" row with a
 *     per-automation [Pause].
 *   [Not now]      → dismisses that proposal id for 14 days.
 *
 * Honest by construction: no signal → no card, no filler. Evidence lines
 * are the detector's real numbers, never copywriting.
 */

import { useEffect, useState } from 'react';
import { Check, HeartPulse, Pause, X } from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { useOptionalToast } from './ToastProvider';
import { C } from '../lib/tokens';
import {
  detectFollowUpDiscipline,
  detectPastDueChasing,
  detectWeeklyUpdateCadence,
  isDismissed,
  pickHeartbeatProposal,
  sameLocalDay,
  type AutomationId,
  type HeartbeatDetector,
  type HeartbeatProposal,
} from '../lib/agent/heartbeat';
import {
  dismissHeartbeatProposal,
  enableAutomation,
  listAutomations,
  markHeartbeatProposed,
  pauseAutomation,
  readHeartbeatState,
  subscribeAutomations,
  type AutomationRecord,
} from '../lib/agent/automations';
import type { Account, Person } from '../lib/db/types';

export interface HeartbeatCardProps {
  people: Person[];
  accounts: Account[];
  /** False for viewers — Heartbeat only talks to people who can act. */
  canMutate: boolean;
  /** Injectable clock for deterministic tests; defaults to now. */
  now?: number;
}

export function HeartbeatCard({ people, accounts, canMutate, now }: HeartbeatCardProps) {
  const toast = useOptionalToast();
  const [proposal, setProposal] = useState<HeartbeatProposal | null>(null);
  const [automations, setAutomations] = useState<AutomationRecord[]>([]);
  const [busy, setBusy] = useState(false);

  // The registry row is live — enabling/pausing updates everywhere.
  useEffect(() => subscribeAutomations(setAutomations), []);

  useEffect(() => {
    if (!canMutate) return;
    let active = true;
    const nowMs = now ?? Date.now();
    (async () => {
      const [state, autos] = await Promise.all([readHeartbeatState(), listAutomations()]);
      if (!active) return;
      const enabledIds = new Set(autos.filter((a) => a.enabled).map((a) => a.id));
      const detectorFor: Record<AutomationId, HeartbeatDetector> = {
        'past-due-reminders': () => detectPastDueChasing(people, nowMs),
        'followup-sweep': () => detectFollowUpDiscipline(people, nowMs),
        'weekly-updates': () => detectWeeklyUpdateCadence(accounts, people),
        // Default-enabled (drafting is safe) — Heartbeat never proposes it.
        'content-autopilot': () => null,
      };
      // An enabled automation is never re-proposed — fold it into the
      // dismissal map the pure picker already understands.
      const suppressed: Record<string, string> = { ...state.dismissed };
      for (const id of enabledIds) suppressed[id] = new Date(nowMs).toISOString();

      // Same-day reload: re-show TODAY's proposal (fresh evidence from the
      // live records) instead of counting it as a second proposal.
      const lastAt = state.lastProposedAt ? Date.parse(state.lastProposedAt) : NaN;
      if (
        state.lastProposedId &&
        !Number.isNaN(lastAt) &&
        sameLocalDay(lastAt, nowMs) &&
        !enabledIds.has(state.lastProposedId) &&
        !isDismissed(state.lastProposedId, state.dismissed, nowMs)
      ) {
        if (active) setProposal(detectorFor[state.lastProposedId]());
        return;
      }

      const picked = pickHeartbeatProposal(
        [
          detectorFor['past-due-reminders'],
          detectorFor['followup-sweep'],
          detectorFor['weekly-updates'],
        ],
        suppressed,
        nowMs,
        state.lastProposedAt,
      );
      if (picked) {
        await markHeartbeatProposed(picked.id, new Date(nowMs).toISOString());
      }
      if (active) setProposal(picked);
    })().catch(() => undefined);
    return () => {
      active = false;
    };
  }, [people, accounts, canMutate, now]);

  const activeAutomations = automations.filter((a) => a.enabled);

  const approve = async () => {
    if (!proposal || busy) return;
    setBusy(true);
    try {
      const rec = await enableAutomation(proposal.id);
      toast?.push({
        kind: 'success',
        text: `Automation on: ${rec.name}`,
        detail: 'You approve every send — pause it any time from this card.',
      });
      setProposal(null);
    } finally {
      setBusy(false);
    }
  };

  const notNow = async () => {
    if (!proposal || busy) return;
    setBusy(true);
    try {
      await dismissHeartbeatProposal(proposal.id, new Date(now ?? Date.now()).toISOString());
      setProposal(null);
    } finally {
      setBusy(false);
    }
  };

  if (!canMutate) return null;
  if (!proposal && activeAutomations.length === 0) return null;

  return (
    <GlassPanel
      variant="subtle"
      animate={false}
      style={{ padding: '12px 16px' }}
      aria-label="Heartbeat"
    >
      {proposal && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span
              className="glass-pill glass-pill--magenta"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
            >
              <HeartPulse size={11} />
              Heartbeat
            </span>
            <span className="t-meta" style={{ color: C.textDim }}>
              one proposal per day · scoped · revocable — never a global switch
            </span>
          </div>
          <span className="t-card-title" style={{ display: 'block', fontSize: 14 }}>
            {proposal.headline}
          </span>
          <ul style={{ margin: '6px 0', paddingLeft: 18 }}>
            {proposal.evidence.map((line) => (
              <li key={line} className="t-meta" style={{ color: C.textDim, margin: '2px 0' }}>
                {line}
              </li>
            ))}
          </ul>
          <p className="t-meta" style={{ margin: '0 0 8px' }}>
            {proposal.whatItWillDo}{' '}
            <span className="glass-pill" style={{ marginLeft: 4 }}>{proposal.cadenceLabel}</span>
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="glass-btn"
              disabled={busy}
              onClick={() => void approve()}
              title="Turns on this ONE automation — its drafts still queue for your approval"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                border: '1px solid rgba(0,255,128,0.3)',
              }}
            >
              <Check size={12} /> Approve once
            </button>
            <button
              type="button"
              className="glass-btn"
              disabled={busy}
              onClick={() => void notNow()}
              title="Dismisses this proposal — it won't come back for 14 days"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <X size={12} /> Not now
            </button>
          </div>
        </div>
      )}

      {activeAutomations.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            marginTop: proposal ? 10 : 0,
          }}
        >
          <span className="glass-pill glass-pill--green">
            Active automations ({activeAutomations.length})
          </span>
          {activeAutomations.map((a) => (
            <span
              key={a.id}
              className="t-meta"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              {a.name}
              <button
                type="button"
                className="glass-btn"
                onClick={() => void pauseAutomation(a.id)}
                title={`Pause ${a.name} — it stops running until re-approved`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px' }}
                aria-label={`Pause ${a.name}`}
              >
                <Pause size={10} /> Pause
              </button>
            </span>
          ))}
        </div>
      )}
    </GlassPanel>
  );
}
